import { useNavigation, useRoute } from "@react-navigation/native";
import "firebase/firestore";
import "firebase/storage";
import { CSSProperties, FC, useCallback, useEffect, useState } from "react";
import { ActivityIndicator, ScrollView, StyleSheet, Text, TouchableOpacity, View } from "react-native";
import { useSelector } from "react-redux";

import * as connectionDb from "src/db/connection.db";
import { kRoute } from "src/kRoute";
import { saveSettingsPage } from "src/redux/company/actions";
import { uploadProfileImage, uploadRepTaxInfo } from "src/redux/AdminSettings/actions";
import { getDefaultRepProfile } from "src/redux/company/defaultData";
import { tRepSettingsPath } from "src/redux/company/types";
import { tReduxState } from "src/redux/types";
import { logout, sendResetPasswordEmail } from "src/redux/user/actions";
import { Spacer } from "../components";
import { Import_Sm } from "../components/impComponents";
import { tValidType, ValidatedTextFormInput } from "./FormInputs";
import { kColors } from "src/kColors";
import { kFonts } from "src/kFonts";
import { kStyleConst } from "src/kStyle";
import { useFetchCompanySettings } from "src/redux/AdminSettings/actions";
import { setBackButtonRoute } from "src/redux/navigation/thunkActions";
import AdminUserPanel from "./AdminUserPanel";
import { PdImagePicker, tImagePickerValue } from "./forms/picker";
import { tRep, tVendorData } from "tmt_api/src/types/types.validator";
import { PdButton, PdButtonLabel } from "./forms/button";
import { useConfirmModal, useVendorSetupAccount } from "src/hooks/modals";
import { useAppDispatch } from "src/redux";
import { openUrlInNewTab } from "src/util/linking";
import { useIsMobile } from "src/hooks/ui";
import { PdStack } from "./layout";

const nameSelector = ({ AdminSettingReducer }: tReduxState) =>
  AdminSettingReducer.type === "rep" ? AdminSettingReducer.name : getDefaultRepProfile().name;

const linkedInSelector = ({ AdminSettingReducer }: tReduxState) =>
  AdminSettingReducer.type === "rep"
    ? AdminSettingReducer.socialMedia?.linkedin ?? AdminSettingReducer.links?.linkedin ?? ""
    : getDefaultRepProfile().socialMedia?.linkedin ?? "";

const instagramSelector = ({ AdminSettingReducer }: tReduxState) =>
  AdminSettingReducer.type === "rep"
    ? AdminSettingReducer.socialMedia?.instagram ?? AdminSettingReducer.links?.instagram ?? ""
    : getDefaultRepProfile().socialMedia?.instagram ?? "";

const facebookSelector = ({ AdminSettingReducer }: tReduxState) =>
  AdminSettingReducer.type === "rep"
    ? AdminSettingReducer.socialMedia?.facebook ?? AdminSettingReducer.links?.facebook ?? ""
    : getDefaultRepProfile().socialMedia?.facebook ?? "";

const twitterSelector = ({ AdminSettingReducer }: tReduxState) =>
  AdminSettingReducer.type === "rep"
    ? AdminSettingReducer.socialMedia?.twitter ?? AdminSettingReducer.links?.twitter ?? ""
    : getDefaultRepProfile().socialMedia?.twitter ?? "";

const aboutMeSelector = ({ AdminSettingReducer }: tReduxState) =>
  AdminSettingReducer.type === "rep" ? AdminSettingReducer.aboutMe : getDefaultRepProfile().aboutMe;

const companySelector = ({ AdminSettingReducer }: tReduxState) =>
  AdminSettingReducer.type === "rep" ? AdminSettingReducer.company : getDefaultRepProfile().company;

const phoneSelector = ({ AdminSettingReducer }: tReduxState) =>
  AdminSettingReducer.type === "rep" ? AdminSettingReducer.phone : getDefaultRepProfile().phone;

const emailSelector = ({ AdminSettingReducer }: tReduxState) =>
  AdminSettingReducer.type === "rep" ? AdminSettingReducer.email : getDefaultRepProfile().email;

const address1Selector = ({ AdminSettingReducer }: tReduxState) =>
  AdminSettingReducer.type === "rep" ? AdminSettingReducer.address.address1 : getDefaultRepProfile().address.address1;

const address2Selector = ({ AdminSettingReducer }: tReduxState) =>
  AdminSettingReducer.type === "rep"
    ? AdminSettingReducer.address.address2
    : getDefaultRepProfile().address.address2 ?? "";

const citySelector = ({ AdminSettingReducer }: tReduxState) =>
  AdminSettingReducer.type === "rep" ? AdminSettingReducer.address.city : getDefaultRepProfile().address.city;

const stateOrProvinceSelector = ({ AdminSettingReducer }: tReduxState) =>
  AdminSettingReducer.type === "rep"
    ? AdminSettingReducer.address.state_or_province
    : getDefaultRepProfile().address.state_or_province;

const postalCodeSelector = ({ AdminSettingReducer }: tReduxState) =>
  AdminSettingReducer.type === "rep"
    ? AdminSettingReducer.address.postal_code
    : getDefaultRepProfile().address.postal_code;

const taxIdSelector = ({ AdminSettingReducer }: tReduxState) =>
  AdminSettingReducer.type === "rep" ? AdminSettingReducer.taxId : getDefaultRepProfile().taxId;

type tAdminRepSettingsParams = {
  params: { repId?: string };
  key: string;
  name: string;
};

const Content: FC<{ repSettings: tRep & { type: "rep" } }> = ({ repSettings }) => {
  const navigation = useNavigation();
  const dispatch = useAppDispatch();
  const isMobile = useIsMobile();
  const rAdminSettings = useSelector((state) => state.AdminSettingReducer);
  const rIsProfileUpdating = useSelector((state) => state.LoadingReducer.profileUpdating);
  const rCompanyId = useSelector((state) => state.UserReducer.profile.companyRefPath.split("/")?.[1]);
  const rIsAdmin = useSelector((state) => state.UserReducer.profile.accountType === "tmtAdmin");
  const rVendorId = useSelector((state) => state.CompanyReducer.type === "rep" && !!state.CompanyReducer.vendorId);
  const rVendorIdAdmin = useSelector(
    (state) => state.AdminSettingReducer.type === "rep" && !!state.AdminSettingReducer.vendorId,
  );
  const vendorSetupAccount = useVendorSetupAccount();
  const confirmModal = useConfirmModal();

  const [sIsValid, setIsValid] = useState<tValidType>({});

  const isSetupPayments = rVendorId || rVendorIdAdmin;

  const handleOnChangeProfileImage = async ({ file }: tImagePickerValue) => {
    await dispatch(uploadProfileImage(rAdminSettings, file));
    dispatch({
      type: "RIBBON",
      message: repSettings.avatar
        ? "Successfully uploaded profile image."
        : "Successfully uploaded profile image. Be sure to Save before leaving page.",
    });
  };

  const saveSettings = useCallback(() => {
    dispatch(saveSettingsPage(rAdminSettings));
  }, [dispatch, rAdminSettings]);

  const handleOnPressChangePassword = async () => {
    const response = await confirmModal.openAsync({
      title: "Reset Password",
      description:
        "For your security, in order to reset your password, you will be logged out. Would you like to continue?",
    });
    if (response.status === "closed") return;

    dispatch(sendResetPasswordEmail(repSettings.email));
    dispatch(logout());
  };

  const onChangePostalCode = useCallback(
    (myPostalCode) => {
      dispatch({ type: "ADMIN_UPDATE_REP_SETTINGS", settingsPart: { address: { postal_code: myPostalCode } } });
    },
    [dispatch],
  );
  const saveTaxInfo = useCallback(() => {
    dispatch(uploadRepTaxInfo(rAdminSettings));
  }, [dispatch, rAdminSettings]);

  const onPressTaxInfo = () => {
    openUrlInNewTab(repSettings.taxInfo?.url ?? "");
  };

  const handleOnPressViewMyStore = async () => {
    const connectionsQuery = await connectionDb.queryBrandRepConnection({ repId: rCompanyId }).get();
    if (connectionsQuery.size > 1) {
      navigation.navigate(kRoute.P99);
    } else if (connectionsQuery.docs[0]) {
      navigation.navigate(kRoute.P100, { brand: connectionsQuery.docs[0].data().business.name });
    }
  };

  const handleOnPressSetupAccount = () => {
    const defaultValue: tVendorData = {
      businessName: repSettings?.name ?? "",
      businessDomain: "",
      ein: repSettings?.taxId ?? "",
      url: "",
      emailAddress: repSettings?.email ?? "",
      address1: repSettings.address.address1,
      address2: repSettings.address.address2 ?? "",
      city: repSettings.address.city,
      state: repSettings.address.state_or_province,
      zipCode: repSettings.address.postal_code,
      phone: repSettings.phone ?? "",
      country: repSettings.address.country_code,
      accountNumber: "",
      routingNumber: "",
    };
    vendorSetupAccount.openAsync({ mode: "rep", defaultValue, isEditing: isSetupPayments });
  };

  return (
    <ScrollView style={styles.P09_StackScreenDefault}>
      <View style={[styles.P09_PageContainer, styles.P09_ZeroTop]}>
        <View style={[styles.gSpaceBetweenRow, styles.gHeaderSpacing]}>
          <View style={styles.rowColumn}>
            <Text style={styles.gFormHeader}>Public Profile Information</Text>
            <Text>This information will be visible to brands after connection is made.</Text>
          </View>
          <PdButton onPress={handleOnPressViewMyStore}>
            <PdButtonLabel>View My Store</PdButtonLabel>
          </PdButton>
        </View>
        <View style={styles.formControl}>
          <View style={styles.P09_Row}>
            <ValidatedTextFormInput<tRepSettingsPath>
              wrapperStyle={styles.gFormRowNoMarginValidatedSingle}
              titleViewStyle={styles.rowNoMargin}
              titleProps={{ style: styles.P08_01_InputHeader }}
              dispatchAction={{ type: "ADMIN_UPDATE_REP_SETTINGS", settingsPart: { name: "" } }}
              dispatchPath="settingsPart.name"
              selectorFunction={nameSelector}
              controller={sIsValid}
              setController={setIsValid}
              myTitle="Name*"
              uniqueName="name"
              textInputProps={{
                style: styles.gLinkBankInput,
                placeholder: "Name",
              }}
            />
          </View>
          <View style={styles.P09_Row}>
            <ValidatedTextFormInput<tRepSettingsPath>
              wrapperStyle={styles.gFormRowNoMarginValidatedSingle}
              titleViewStyle={styles.rowNoMargin}
              titleProps={{ style: styles.P08_01_InputHeader }}
              dispatchAction={{ type: "ADMIN_UPDATE_REP_SETTINGS", settingsPart: { socialMedia: { linkedin: "" } } }}
              dispatchPath="settingsPart.socialMedia.linkedin"
              selectorFunction={linkedInSelector}
              controller={sIsValid}
              setController={setIsValid}
              myTitle="LinkedIn"
              uniqueName="linkedIn"
              rules={{
                pattern: {
                  pattern: /^(http:\/\/|https:\/\/).*$/g,
                  message: "Please add https:// or http:// before your URL",
                },
              }}
              textInputProps={{
                style: styles.gLinkBankInput,
                placeholder: "LinkedIn account",
              }}
            />
            <Spacer />
            <ValidatedTextFormInput<tRepSettingsPath>
              wrapperStyle={styles.gFormRowNoMarginValidatedSingle}
              titleViewStyle={styles.rowNoMargin}
              titleProps={{ style: styles.P08_01_InputHeader }}
              dispatchAction={{ type: "ADMIN_UPDATE_REP_SETTINGS", settingsPart: { socialMedia: { instagram: "" } } }}
              dispatchPath="settingsPart.socialMedia.instagram"
              selectorFunction={instagramSelector}
              controller={sIsValid}
              setController={setIsValid}
              myTitle="Instagram"
              uniqueName="instagram"
              rules={{
                pattern: {
                  pattern: /^(http:\/\/|https:\/\/).*$/g,
                  message: "Please add https:// or http:// before your URL",
                },
              }}
              textInputProps={{
                style: styles.gLinkBankInput,
                placeholder: "Instagram account",
              }}
            />
            <Spacer />
            <ValidatedTextFormInput<tRepSettingsPath>
              wrapperStyle={styles.gFormRowNoMarginValidatedSingle}
              titleViewStyle={styles.rowNoMargin}
              titleProps={{ style: styles.P08_01_InputHeader }}
              dispatchAction={{ type: "ADMIN_UPDATE_REP_SETTINGS", settingsPart: { socialMedia: { facebook: "" } } }}
              dispatchPath="settingsPart.socialMedia.facebook"
              selectorFunction={facebookSelector}
              controller={sIsValid}
              setController={setIsValid}
              myTitle="Facebook"
              uniqueName="facebook"
              rules={{
                pattern: {
                  pattern: /^(http:\/\/|https:\/\/).*$/g,
                  message: "Please add https:// or http:// before your URL",
                },
              }}
              textInputProps={{
                style: styles.gLinkBankInput,
                placeholder: "Facebook account",
              }}
            />
            <Spacer />
            <ValidatedTextFormInput<tRepSettingsPath>
              wrapperStyle={styles.gFormRowNoMarginValidatedSingle}
              titleViewStyle={styles.rowNoMargin}
              titleProps={{ style: styles.P08_01_InputHeader }}
              dispatchAction={{ type: "ADMIN_UPDATE_REP_SETTINGS", settingsPart: { socialMedia: { twitter: "" } } }}
              dispatchPath="settingsPart.socialMedia.twitter"
              selectorFunction={twitterSelector}
              controller={sIsValid}
              setController={setIsValid}
              myTitle="Twitter"
              uniqueName="twitter"
              textInputProps={{
                style: styles.gLinkBankInput,
                placeholder: "Twitter account",
              }}
            />
          </View>
          <View style={styles.P09_Row}>
            <ValidatedTextFormInput<tRepSettingsPath>
              wrapperStyle={styles.gFormRowNoMarginValidatedSingle}
              titleViewStyle={styles.rowNoMargin}
              titleProps={{ style: styles.P08_01_InputHeader }}
              dispatchAction={{ type: "ADMIN_UPDATE_REP_SETTINGS", settingsPart: { aboutMe: "" } }}
              dispatchPath="settingsPart.aboutMe"
              selectorFunction={aboutMeSelector}
              controller={sIsValid}
              setController={setIsValid}
              myTitle="About Me"
              uniqueName="aboutMe"
              textInputProps={{
                style: styles.gLinkBankInputBox,
                placeholder: "Give a brief bio about your business.",
                multiline: true,
                numberOfLines: 8,
              }}
            />
            <Spacer width={33} />
            <View style={styles.P09_ProfileImageContainer}>
              <Text style={styles.P09_FormTitle}>Profile Image*</Text>
              <PdImagePicker
                style={styles.P09_ImageSelectRound}
                mobileStyle={styles.P09_ImageRound}
                webStyle={webStyles.P09_ImageRound}
                source={{ uri: repSettings.avatar }}
                onChange={handleOnChangeProfileImage}
              />
            </View>
          </View>
        </View>
      </View>
      <View style={styles.P09_PageContainer}>
        <View style={[styles.gSpaceBetweenRow, styles.gHeaderSpacing]}>
          <View style={styles.rowColumn}>
            <Text style={styles.gFormHeader}>Personal Account Information</Text>
            <Text>This information is private and will not be shared</Text>
          </View>
        </View>
        <View style={styles.formControl}>
          <View style={styles.P09_Row}>
            <ValidatedTextFormInput<tRepSettingsPath>
              wrapperStyle={styles.gFormRowNoMarginValidatedSingle}
              titleViewStyle={styles.rowNoMargin}
              titleProps={{ style: styles.P08_01_InputHeader }}
              dispatchAction={{ type: "ADMIN_UPDATE_REP_SETTINGS", settingsPart: { company: "" } }}
              dispatchPath="settingsPart.company"
              selectorFunction={companySelector}
              controller={sIsValid}
              setController={setIsValid}
              myTitle="Business Name*"
              uniqueName="company"
              textInputProps={{
                style: styles.gLinkBankInput,
                placeholder: "Business Name",
              }}
            />
          </View>
          <View style={styles.P09_Row}>
            <ValidatedTextFormInput<tRepSettingsPath>
              wrapperStyle={styles.gFormRowNoMarginValidatedSingle}
              titleViewStyle={styles.rowNoMargin}
              titleProps={{ style: styles.P08_01_InputHeader }}
              dispatchAction={{ type: "ADMIN_UPDATE_REP_SETTINGS", settingsPart: { phone: "" } }}
              dispatchPath="settingsPart.phone"
              selectorFunction={phoneSelector}
              controller={sIsValid}
              setController={setIsValid}
              myTitle="Phone Number*"
              uniqueName="phone"
              textInputProps={{
                style: styles.gLinkBankInput,
                placeholder: "Phone Number",
              }}
            />
            <Spacer />
            <ValidatedTextFormInput<tRepSettingsPath>
              wrapperStyle={styles.gFormRowNoMarginValidatedSingle}
              titleViewStyle={styles.rowNoMargin}
              titleProps={{ style: styles.P08_01_InputHeader }}
              dispatchAction={{ type: "ADMIN_UPDATE_REP_SETTINGS", settingsPart: { email: "" } }}
              dispatchPath="settingsPart.email"
              selectorFunction={emailSelector}
              controller={sIsValid}
              setController={setIsValid}
              myTitle="Email*"
              uniqueName="email"
              textInputProps={{
                style: styles.gLinkBankInputGray,
                placeholder: "Email",
                editable: false,
              }}
            />
          </View>
          <View style={styles.P09_Row}>
            <ValidatedTextFormInput<tRepSettingsPath>
              wrapperStyle={styles.gFormRowNoMarginValidatedSingle}
              titleViewStyle={styles.rowNoMargin}
              titleProps={{ style: styles.P08_01_InputHeader }}
              dispatchAction={{ type: "ADMIN_UPDATE_REP_SETTINGS", settingsPart: { address: { address1: "" } } }}
              dispatchPath="settingsPart.address.address1"
              selectorFunction={address1Selector}
              controller={sIsValid}
              setController={setIsValid}
              myTitle="Address*"
              uniqueName="address1"
              textInputProps={{
                style: styles.gLinkBankInput,
                placeholder: "Address 1",
              }}
            />
          </View>
          <View style={styles.P09_Row}>
            <ValidatedTextFormInput<tRepSettingsPath>
              wrapperStyle={styles.gFormRowNoMarginValidatedSingle}
              titleViewStyle={styles.rowNoMargin}
              titleProps={{ style: styles.P08_01_InputHeader }}
              dispatchAction={{ type: "ADMIN_UPDATE_REP_SETTINGS", settingsPart: { address: { address2: "" } } }}
              dispatchPath="settingsPart.address.address2"
              selectorFunction={address2Selector}
              controller={sIsValid}
              setController={setIsValid}
              myTitle="Address 2*"
              uniqueName="address2"
              textInputProps={{
                style: styles.gLinkBankInput,
                placeholder: "Address 2",
              }}
            />
          </View>
          <View style={styles.P09_Row}>
            <ValidatedTextFormInput<tRepSettingsPath>
              wrapperStyle={styles.gFormRowNoMarginValidatedSingle}
              titleViewStyle={styles.rowNoMargin}
              titleProps={{ style: styles.P08_01_InputHeader }}
              dispatchAction={{ type: "ADMIN_UPDATE_REP_SETTINGS", settingsPart: { address: { city: "" } } }}
              dispatchPath="settingsPart.address.city"
              selectorFunction={citySelector}
              controller={sIsValid}
              setController={setIsValid}
              myTitle="City*"
              uniqueName="city"
              textInputProps={{
                style: styles.gLinkBankInput,
                placeholder: "City",
              }}
            />
            <Spacer />
            <ValidatedTextFormInput<tRepSettingsPath>
              wrapperStyle={styles.gFormRowNoMarginValidatedSingle}
              titleViewStyle={styles.rowNoMargin}
              titleProps={{ style: styles.P08_01_InputHeader }}
              dispatchAction={{
                type: "ADMIN_UPDATE_REP_SETTINGS",
                settingsPart: { address: { state_or_province: "" } },
              }}
              dispatchPath="settingsPart.address.state_or_province"
              selectorFunction={stateOrProvinceSelector}
              controller={sIsValid}
              setController={setIsValid}
              myTitle="State*"
              uniqueName="stateOrProvince"
              textInputProps={{
                style: styles.gLinkBankInput,
                placeholder: "State or Province",
              }}
            />
            <Spacer />
            <ValidatedTextFormInput<tRepSettingsPath>
              wrapperStyle={styles.gFormRowNoMarginValidatedSingle}
              titleViewStyle={styles.rowNoMargin}
              titleProps={{ style: styles.P08_01_InputHeader }}
              dispatchAction={{ type: "ADMIN_UPDATE_REP_SETTINGS", settingsPart: { address: { postal_code: "" } } }}
              dispatchPath="settingsPart.address.postal_code"
              selectorFunction={postalCodeSelector}
              controller={sIsValid}
              setController={setIsValid}
              myTitle="Zipcode*"
              uniqueName="postalCode"
              textInputProps={{
                style: styles.gLinkBankInput,
                placeholder: "Zipcode",
                onChangeText: onChangePostalCode,
              }}
            />
          </View>
        </View>
        <PdStack direction={isMobile ? "vertical" : "horizontal"} style={[styles.alignStart, styles.mt3]}>
          <View style={styles.flex1}>
            <View style={[styles.row, styles.alignCenter]}>
              <Text style={styles.P09_FormTitle}>Resale Certificate Copy</Text>
              <TouchableOpacity onPress={saveTaxInfo}>
                <Import_Sm style={styles.P09_AbsoluteImport} />
              </TouchableOpacity>
            </View>
            <TouchableOpacity onPress={onPressTaxInfo}>
              <Text style={[styles.gFormInput, !repSettings.taxInfo?.url ? styles.gFormInputPlaceholder : {}]}>
                {repSettings.taxInfo?.name !== undefined && repSettings.taxInfo?.name !== ""
                  ? repSettings.taxInfo?.name
                  : "Upload a copy of your Resale Certificate"}
              </Text>
            </TouchableOpacity>
          </View>
          <ValidatedTextFormInput<tRepSettingsPath>
            wrapperStyle={[styles.wFull, styles.flex1]}
            titleViewStyle={styles.rowNoMargin}
            dispatchAction={{ type: "ADMIN_UPDATE_RETAIL_SETTINGS", settingsPart: { taxId: "" } }}
            dispatchPath="settingsPart.taxId"
            selectorFunction={taxIdSelector}
            controller={sIsValid}
            setController={setIsValid}
            titleProps={{ style: styles.P08_01_InputHeader }}
            myTitle="Tax ID #number"
            uniqueName="taxId"
            textInputProps={{
              style: styles.gLinkBankInput,
              placeholder: "Company Tax ID",
            }}
          />
        </PdStack>
        <View style={styles.gHeaderSpacing}>
          <Text style={styles.gFormHeader}>Payment Terms</Text>
          <Text style={styles.underPaymentTermsSpacing}>
            Upon Order Shipment, we submit payment to you right away, directly to your bank account. Please click below
            to connect your account to receive payments
          </Text>
          <PdButton
            variant={isSetupPayments ? "outline" : "solid"}
            onPress={handleOnPressSetupAccount}
            style={styles.mrAuto}
          >
            <PdButtonLabel>{isSetupPayments ? "Edit Terms" : "Setup"}</PdButtonLabel>
          </PdButton>
        </View>
        {!rIsAdmin && (
          <PdStack
            isReversed={isMobile}
            direction={isMobile ? "vertical" : "horizontal"}
            style={[styles.mt6, styles.mb6, styles.justifyBetween]}
          >
            <PdButton variant="outline" onPress={handleOnPressChangePassword} style={styles.minWMd}>
              <PdButtonLabel>Change Password</PdButtonLabel>
            </PdButton>
            <PdButton onPress={saveSettings} isLoading={rIsProfileUpdating} style={styles.minWMd}>
              <PdButtonLabel>Save</PdButtonLabel>
            </PdButton>
          </PdStack>
        )}
      </View>
      {rIsAdmin && <AdminUserPanel />}
    </ScrollView>
  );
};

const RepSettings: FC = () => {
  const route = useRoute<tAdminRepSettingsParams>();
  useFetchCompanySettings(route?.params?.repId, "reps");
  const dispatch = useAppDispatch();
  const isLoading = useSelector((state) => state.LoadingReducer.companyFetching);
  const adminSettings = useSelector((state) => state.AdminSettingReducer);

  useEffect(() => {
    dispatch(setBackButtonRoute(kRoute.P06));
  }, [dispatch]);

  if (
    isLoading ||
    !adminSettings ||
    adminSettings.type === "none" ||
    (adminSettings.type === "rep" && !adminSettings.id)
  ) {
    return (
      <View style={[styles.flex1, styles.alignCenter, styles.justifyCenter]}>
        <ActivityIndicator size="large" color={kColors.brandDarkGreen} />
      </View>
    );
  }

  if (adminSettings.type !== "rep") {
    return null;
  }

  return <Content repSettings={adminSettings} />;
};

export default RepSettings;

const styles = StyleSheet.create({
  P08_01_InputHeader: {
    ...kFonts.formLegend,
  },
  P09_AbsoluteImport: {
    ...kStyleConst.kImportSmall,
    alignSelf: "center",
    marginLeft: 3 * kStyleConst.kSpacing,
  },
  P09_FormTitle: {
    ...kFonts.formLegend,
  },
  P09_ImageRound: {
    ...kStyleConst.kImageSelect,
    borderRadius: 360,
  },
  P09_ImageSelectRound: {
    ...kStyleConst.kImageSelect,
    alignItems: "center",
    backgroundColor: kColors.backgroundGrey,
    borderRadius: 360,
    flexDirection: "column",
    justifyContent: "center",
    marginTop: kStyleConst.kSpacing * 4,
  },
  P09_PageContainer: {
    backgroundColor: kColors.white,
    flexGrow: 1,
    marginRight: 6 * kStyleConst.kSpacing,
    marginTop: 6 * kStyleConst.kSpacing,
    paddingHorizontal: 6 * kStyleConst.kSpacing,
  },
  P09_ProfileImageContainer: {
    marginTop: kStyleConst.kSpacing * 2,
    width: kStyleConst.kImageSelect.width,
  },
  P09_Row: {
    flexDirection: "row",
    flexWrap: "wrap",
    marginVertical: kStyleConst.kSpacing * 2,
    minWidth: kStyleConst.kMinWidth,
  },
  P09_StackScreenDefault: {
    backgroundColor: kColors.brandLight,
    flex: 1,
  },
  P09_ZeroTop: {
    marginTop: 0,
  },
  alignCenter: {
    alignItems: "center",
  },
  alignStart: {
    alignItems: "flex-start",
  },
  flex1: {
    flex: 1,
  },
  formControl: {
    marginBottom: 16,
  },
  gFormHeader: {
    ...kFonts.profileHeader,
    marginBottom: kStyleConst.kSpacing * 2,
  },
  gFormInput: {
    borderBottomWidth: 1,
    borderColor: kColors.brandPink,
    borderRadius: 2,
    flex: 1,
    height: 36,
    marginTop: 5,
    outlineColor: kColors.transparent,
    paddingLeft: kStyleConst.kSpacing,
    paddingRight: 40,
    paddingVertical: 10,
  },
  gFormInputPlaceholder: {
    color: kColors.mediumGrey,
  },
  gFormRowNoMarginValidatedSingle: {
    flexGrow: 1,
    flexWrap: "wrap",
    justifyContent: "space-between",
    marginBottom: 3 * kStyleConst.kSpacing,
    marginTop: 6 * kStyleConst.kSpacing,
    minWidth: kStyleConst.kApplyMinWidth,
  },
  gHeaderSpacing: {
    marginVertical: 15,
  },
  gLinkBankInput: {
    borderBottomWidth: 1,
    borderColor: kColors.brandPink,
    borderRadius: 2,
    flex: 1,
    height: 36,
    marginLeft: 0,
    marginTop: 5,
    minWidth: kStyleConst.kApplyMinWidth,
    outlineColor: kColors.transparent,
    paddingLeft: kStyleConst.kSpacing,
    paddingVertical: 2 * kStyleConst.kSpacing,
  },
  gLinkBankInputBox: {
    borderColor: kColors.brandPink,
    borderRadius: 2,
    borderWidth: 1,
    flex: 1,
    height: kStyleConst.kApplicationNotesBoxHeight,
    marginTop: 5,
    minWidth: kStyleConst.kApplyMinWidth,
    outlineColor: kColors.transparent,
    outlineWidth: 0,
    paddingLeft: 2 * kStyleConst.kSpacing,
    paddingVertical: 2 * kStyleConst.kSpacing,
  },
  gLinkBankInputGray: {
    borderBottomWidth: 1,
    borderColor: kColors.brandPink,
    borderRadius: 2,
    color: kColors.mediumGrey,
    flex: 1,
    height: 36,
    marginTop: 5,
    minWidth: kStyleConst.kApplyMinWidth,
    outlineColor: kColors.transparent,
    paddingLeft: kStyleConst.kSpacing,
    paddingVertical: 2 * kStyleConst.kSpacing,
  },
  gSpaceBetweenRow: {
    alignItems: "baseline",
    flexDirection: "row",
    justifyContent: "space-between",
  },
  justifyBetween: {
    justifyContent: "space-between",
  },
  justifyCenter: {
    justifyContent: "center",
  },
  mb6: {
    marginBottom: 6 * kStyleConst.kSpacing,
  },
  minWMd: {
    minWidth: 150,
  },
  mrAuto: {
    marginRight: "auto",
  },
  mt3: {
    marginTop: 3 * kStyleConst.kSpacing,
  },
  mt6: {
    marginTop: 6 * kStyleConst.kSpacing,
  },
  row: {
    flexDirection: "row",
    position: "relative",
    zIndex: 1000,
  },
  rowColumn: {
    flex: 1,
    marginBottom: 16,
    paddingEnd: 16,
  },
  rowNoMargin: {
    alignItems: "center",
    flexDirection: "row",
    marginTop: 0,
    minHeight: 25,
  },
  underPaymentTermsSpacing: { marginBottom: 7 * kStyleConst.kSpacing },
  wFull: {
    width: "100%",
  },
});

const webStyles: { [key: string]: CSSProperties } = {
  P09_ImageRound: {
    ...kStyleConst.kImageSelect,
    borderRadius: 360,
  },
} as const;
